"use client";

/**
 * @module Cart/_components/PopUpCartItem
 * @description A component that renders an individual item in the popup cart with functionality
 * for quantity adjustment, deletion with undo capability, and price display.
 */

import { useEffect, useState } from "react";
import { formatToUSD } from "@/app/utils/utility";
import { Button } from "@/components/ui/button";
import { Caption, Paragraph, Title } from "@/components/ui/typography";
import { Trash2, TriangleAlert } from "lucide-react";
import QuantityButton from "@/components/ui/quantity-button";
import Image from "next/image";

import Link from "next/link";
import { Separator } from "@/components/ui/separator";
import { Flash } from "@/lib/SystemIcons";

/**
 * PopUpCartItem component that displays a single cart item with image, details, quantity controls and delete functionality.
 *
 * @function PopUpCartItem
 * @param {Object} props - Component props
 * @param {Object} props.productDetails - Details of the product in cart
 * @param {string} props.productDetails.id - Unique identifier of the product
 * @param {string} props.productDetails.name - Name of the product
 * @param {string} props.productDetails.modelNumber - Model number of the product
 * @param {number} props.productDetails.quantity - Quantity of product in cart
 * @param {number} props.productDetails.stockLeft - Remaining stock of the product
 * @param {Object} props.productDetails.img - Product image details
 * @param {string} props.productDetails.img.imagePath - URL of the product image
 * @param {string} props.productDetails.img.imageAlt - Alt text for product image
 * @param {Object} props.productDetails.discountedPrice - Price details
 * @param {string} props.productDetails.discountedPrice.formattedValue - Formatted price string
 * @param {number} props.productDetails.discountedPrice.rawValue - Raw price value
 * @param {boolean} props.showSeparator - Whether to show separator below item
 * @param {Function} props.onChange - Callback when quantity changes
 * @param {Function} props.onUndo - Callback when delete is undone
 * @param {Function} props.onDeleteConfirm - Callback when delete is confirmed
 * @param {Function} props.onDeleteStart - Callback when delete starts
 * @param {Function} props.onProductLabelClick - Callback when product name is clicked
 * @returns {JSX.Element} The rendered cart item component
 *
 * @example
 * // Basic usage
 * <PopUpCartItem
 *   productDetails={{
 *     id: "123",
 *     name: "Product Name",
 *     quantity: 1,
 *     stockLeft: 50,
 *     img: {
 *       imagePath: "/image.jpg",
 *       imageAlt: "Product Image"
 *     },
 *     discountedPrice: {
 *       formattedValue: "$99.99",
 *       rawValue: 99.99
 *     }
 *   }}
 *   onChange={(details) => console.log(details)}
 * />
 *
 * @remarks
 * - Uses a delete animation with undo capability
 * - Shows stock warning when stock is below 30
 * - Responsive layout for mobile, tablet and desktop
 * - Quantity can be adjusted within available stock limits
 * - Price updates automatically with quantity changes
 *
 * @see {@link Cart/_components/PopUpCart} - Parent component
 * @see {@link components/ui/quantity-button} - Quantity control component
 */
export default function PopUpCartItem(props) {
  const [isDelete, setIsDelete] = useState({
    start: false,
    state: false,
    confirmDeleteStart: false,
    confirmDelete: true,
  });
  const [inputQuantity, setinputQuantity] = useState(1);

  const product = props.productDetails || {};

  useEffect(() => {
    let confirmationStart;
    if (isDelete.confirmDeleteStart) {
      confirmationStart = setTimeout(() => {
        setIsDelete(prevIsDelete => ({
          ...prevIsDelete,
          confirmDelete: true,
        }));
        if (isDelete.state) props.onDeleteConfirm(product.id);
      }, 1000);
    }
    return () => clearTimeout(confirmationStart);
  }, [isDelete.confirmDeleteStart, isDelete.state, product.id, props]);

  const handleDelete = () => {
    setIsDelete({ start: true, state: false, confirmDeleteStart: false });
    const undoTimeOut = setTimeout(() => {
      setIsDelete({ start: false, state: true, confirmDeleteStart: true });
      clearTimeout(undoTimeOut);
    }, [300]);
    props.onDeleteStart(product.id);
  };

  function handleQuantityChange(actionType = "") {
    const quantity =
      typeof actionType === "number"
        ? actionType
        : actionType === "add"
          ? product?.quantity + 1
          : product?.quantity - 1;
    props.onChange({ productId: product.id, quantity });
  }

  const handleUndo = () => {
    setIsDelete({
      start: false,
      state: false,
      confirmDeleteStart: false,
      confirmDelete: false,
    });
    props.onUndo(product.id);
  };

  if (isDelete.state) {
    return (
      <div
        className={`h-[146px] flex-col flex items-center justify-center border-b border-midnightIndigo-200 ${
          isDelete.confirmDelete && "animate-delete-item opacity-50 transition duration-200"
        }`}
      >
        <Paragraph size="sm">This item has been deleted</Paragraph>
        <Button variant="outline" size="sm" className="mt-2" onClick={handleUndo}>
          Undo
        </Button>
      </div>
    );
  } else {
    return (
      <>
        <div
          className={`flex lg:px-4 md:items-center lg:items-center gap-2 align-middle border-midnightIndigo-200 rounded py-4 ${
            isDelete.start && "animate-delete-item opacity-50 transition duration-200"
          }`}
        >
          <div className="flex justify-center rounded mr-3">
            <div className="relative flex bg-system-lightergrey lg:h-[148px] lg:w-[112px] md:h-[99px] md:w-[75px]  h-[85px] w-[64px] justify-center items-center rounded">
              {product?.isFlash ? (
                <div
                  className="absolute text-sm top-1 left-1 flex flex-row items-center justify-center gap-1 bg-system-orange rounded px-2 py-1"
                  style={{ width: "72px" }}
                >
                  <Flash className="h-5 w-5 text-system-crystalWhite" />
                  <span className="text-system-crystalWhite mt-1">Sale</span>
                </div>
              ) : null}
              <Image
                src={product?.img?.imagePath}
                width={78}
                height={103}
                alt={product?.img?.imageAlt}
                className=" h-full w-full object-contain   aspect-auto"
              />
            </div>
          </div>
          <div className="grid content-between grid-cols-3 gap-4 w-full lg:h-[148px]">
            <div className="col-span-3 md:col-span-1 lg:col-span-3 flex justify-between ">
              <div>
                <Caption size="xs">{product?.modelNumber || "-"}</Caption>
                <Title size="xs" className="line-clamp-2">
                  <Link
                    href={`/product/${encodeURIComponent(product?.name)}`}
                    onClick={() => props.onProductLabelClick(product.id)} // Ensure you are passing the product id.
                  >
                    {product.name}
                  </Link>
                </Title>

                {product.item_left && (
                  <Caption size="xs" variant="regular" className="text-system-warning flex items-center">
                    <TriangleAlert className="mr-1 h-[20px] w-[20px]" />
                    {product.item_left} left in Stock
                  </Caption>
                )}
              </div>

              <Button
                variant="outline"
                size="icon"
                className={
                  " block md:hidden lg:block p-[4px] text-midnightIndigo-300 hover:text-system-error h-[30px] w-[30px]"
                }
                onClick={handleDelete}
              >
                <Trash2 className="h-[20px] w-[20px] " />
              </Button>
            </div>

            <div className="order-last md:order-1 lg:order-last flex items-center gap-2 col-span-2 md:col-span-1 justify-end md:justify-center lg:justify-end">
              <div className="  w-[96px] h-[35px] lg:w-[110px] ">
                <QuantityButton
                  product={product}
                  quantity={product?.quantity}
                  inStock={product?.stockLeft}
                  onQuantityChange={handleQuantityChange}
                  setinputQuantity={setinputQuantity}
                />
              </div>
            </div>

            <div className="flex items-center justify-start lg:justify-start md:justify-end   col-span-1  md:col-span-1 lg:col-span-2 order-2 md:order-last lg:order-2">
              <div className="flex flex-col items-start ">
                <Caption size="xs" className=" text-midnightIndigo-300 leading-[130%]">
                  {product?.isFlash == null ? product?.discountedPrice?.formattedValue : product?.isFlash} x{" "}
                  {product?.quantity}
                </Caption>
                <Paragraph size="sm" className="text-midnightIndigo-400 ">
                  {formatToUSD(
                    (product?.isFlash == null ? product?.discountedPrice?.rawValue : product?.isFlash) *
                      product?.quantity
                  )}
                </Paragraph>
              </div>
              <Button
                variant="outline"
                size="icon"
                className={
                  "ml-2 hidden md:block lg:hidden  p-[6px] text-midnightIndigo-300 hover:text-system-error h-[30px] w-[30px]"
                }
                onClick={handleDelete}
              >
                <Trash2 className="h-[15px] w-[15px] " />
              </Button>
            </div>
          </div>
        </div>
        {props.showSeparator && <Separator className="w-full h-[1px] " />}
      </>
    );
  }
}

PopUpCartItem.defaultProps = {
  onChange: () => {},
  onUndo: () => {},
  onDeleteConfirm: () => {},
  onDeleteStart: () => {},
};
